import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'
import {
  getServicos,
  getProfissionaisDoServico,
  getConfiguracoes,
  getSlotsDisponiveis,
  getSlotsDisponiveisTodos,
  escolherSalaLivre,
  criarMarcacaoPublica,
  iniciarPagamento,
} from '../lib/supabase'
import { validarTelefone } from '../lib/validacoes'
import { MODO_TESTE } from '../config/modoTeste'
import { NotaTeste } from '../components/AvisoTeste'
import { fmtEur } from '../lib/valores'
import { useTenant } from '../contexts/TenantContext'
import { useToast } from '../contexts/ToastContext'
import Loading from '../components/Loading'

const PASSOS = ['Serviço', 'Profissional', 'Data', 'Hora', 'Confirmar']

// Sinal a cobrar por um serviço: valor fixo do serviço (se definido), senão a
// percentagem base das configurações sobre o preço. Arredondado aos cêntimos.
function calcularSinal(servico, config) {
  if (!servico) return 0
  if (servico.sinal_valor != null && servico.sinal_valor !== '') return Number(servico.sinal_valor)
  const pct = parseFloat(config?.stripe_sinal_percentagem)
  if (!Number.isFinite(pct)) return 0
  return Math.round(Number(servico.preco || 0) * pct) / 100
}

const inputStyle = {
  display: 'block',
  width: '100%',
  minHeight: '48px',
  padding: '12px 14px',
  border: '1px solid var(--cor-borda)',
  borderRadius: 'var(--raio)',
  fontSize: '16px',
  lineHeight: '1.4',
  fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)',
  color: 'var(--cor-texto)',
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  boxSizing: 'border-box',
}

function Campo({ label, value, onChange, type = 'text', placeholder, multiline, maxLength, erro, autoComplete, inputMode, name }) {
  return (
    <div>
      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          autoComplete={autoComplete}
          inputMode={inputMode}
          autoCapitalize={type === 'email' ? 'none' : undefined}
          autoCorrect={type === 'email' ? 'off' : undefined}
          style={{ ...inputStyle, borderColor: erro ? '#EF4444' : undefined }}
        />
      )}
      {erro && (
        <p style={{ marginTop: '4px', fontSize: '12px', color: '#EF4444' }}>{erro}</p>
      )}
    </div>
  )
}

function BarraProgresso({ passo }) {
  return (
    <div style={{ marginBottom: '36px' }}>
      <div style={{ display: 'flex', gap: '5px', marginBottom: '8px' }}>
        {PASSOS.map((_, i) => (
          <div key={i} style={{
            flex: 1,
            height: '4px',
            borderRadius: '2px',
            background: i + 1 <= passo ? 'var(--cor-principal)' : 'var(--cor-creme-escuro)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>
      <p style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', textAlign: 'right' }}>
        Passo <strong style={{ color: 'var(--cor-principal)' }}>{passo}</strong> de {PASSOS.length} — {PASSOS[passo - 1]}
      </p>
    </div>
  )
}

function BotaoNavegacao({ passo, recuar, avancar, podeAvancar }) {
  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      marginTop: '32px',
      justifyContent: passo === 1 ? 'flex-end' : 'space-between',
    }}>
      {passo > 1 && (
        <button onClick={recuar} className="btn-secundario">← Voltar</button>
      )}
      {podeAvancar && (
        <button onClick={avancar} className="btn-primario">Continuar →</button>
      )}
    </div>
  )
}

function cardServico(selecionado) {
  return {
    background: selecionado ? 'var(--cor-creme)' : 'var(--cor-branco)',
    border: `2px solid ${selecionado ? 'var(--cor-principal)' : 'var(--cor-borda)'}`,
    borderRadius: 'var(--raio)',
    padding: '14px 16px',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    transition: 'all 0.15s',
  }
}

function cardProfissional(selecionado) {
  return {
    background: selecionado ? 'var(--cor-creme)' : 'var(--cor-branco)',
    border: `2px solid ${selecionado ? 'var(--cor-principal)' : 'var(--cor-borda)'}`,
    borderRadius: 'var(--raio-lg)',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    transition: 'all 0.15s',
  }
}

// Um serviço mostra a página de apresentação se tiver média ou descrição
// detalhada configuradas no backoffice.
function temApresentacao(s) {
  return !!(s?.midia_url || s?.descricao_detalhada)
}

function diasTrabalhoTexto(dias) {
  const nomes = { 1: 'Dom', 2: 'Seg', 3: 'Ter', 4: 'Qua', 5: 'Qui', 6: 'Sex', 7: 'Sáb' }
  return dias.map(d => nomes[d]).join(', ')
}

export default function Agendar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { tenant } = useTenant()
  const toast = useToast()
  const servicoInicial = location.state?.servico ?? null
  // Vindo da página inicial ("Os Nossos Serviços") com um serviço que tem
  // página de apresentação: mostra primeiro esse detalhe (sub-ecrã do passo 1),
  // tal como no fluxo /agendar. Só salta direto ao passo 2 se não houver detalhe.
  const [passo, setPasso] = useState(
    servicoInicial && !temApresentacao(servicoInicial) ? 2 : 1
  )
  // Serviço cuja apresentação (vídeo/imagem + descrição) está a ser mostrada.
  // É um sub-ecrã do passo 1 (não mexe na contagem da barra de progresso).
  const [apresentacao, setApresentacao] = useState(
    servicoInicial && temApresentacao(servicoInicial) ? servicoInicial : null
  )
  // Garante o autoplay do vídeo de apresentação em qualquer fluxo de entrada.
  // Vindo da página inicial, o vídeo só monta depois do carregamento (fora do
  // gesto de clique) e o atributo `autoPlay` nem sempre dispara — este callback
  // ref força o play() no exato momento em que o <video> entra no DOM.
  // No telemóvel (iOS) o React não aplica de forma fiável o atributo `muted`
  // ao DOM (facebook/react#10389); sem isso o autoplay é bloqueado. Forçamos
  // muted/playsInline na propriedade e voltamos a tentar quando há dados.
  const refVideoApresentacao = useCallback((node) => {
    if (!node) return
    node.muted = true
    node.defaultMuted = true
    node.playsInline = true
    node.setAttribute('muted', '')
    node.setAttribute('playsinline', '')
    const tentarPlay = () => { node.play().catch(() => {}) }
    // No iOS, com a montagem fora do gesto de toque, o carregamento do vídeo é
    // adiado e os eventos de "pronto" só disparam após interação. Forçar load()
    // arranca a transferência; tentamos o play() em todos os marcos de prontidão.
    try { node.load() } catch (e) { /* ignora */ }
    tentarPlay()
    ;['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach(ev =>
      node.addEventListener(ev, tentarPlay, { once: true })
    )
  }, [])
  const [config, setConfig] = useState(null)
  const [servicos, setServicos] = useState([])
  const [profissionais, setProfissionais] = useState([])
  const [carregandoProfissionais, setCarregandoProfissionais] = useState(false)
  const [slots, setSlots] = useState([])
  const [profissionaisPorSlot, setProfissionaisPorSlot] = useState({})
  const [carregando, setCarregando] = useState(true)
  const [carregandoSlots, setCarregandoSlots] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [errosCampos, setErrosCampos] = useState({})
  const [erroData, setErroData] = useState(null)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [turnstileIndisponivel, setTurnstileIndisponivel] = useState(false)
  const [consentimento, setConsentimento] = useState(false)

  const [selecao, setSelecao] = useState({
    servico: servicoInicial,
    profissional: null,
    data: '',
    slot: null,
    cliente: { nome: '', email: '', telefone: '', notas: '', data_nascimento: '', nif: '', newsletter: false },
  })

  useEffect(() => {
    if (!tenant) return
    Promise.all([getServicos(tenant.id), getConfiguracoes(tenant.id)])
      .then(([svcs, cfg]) => {
        setServicos(svcs)
        setConfig(cfg)
      })
      .catch(e => {
        console.error('Erro ao carregar dados:', e)
        toast.erro('Não foi possível carregar os dados. Por favor recarregue a página.')
      })
      .finally(() => setCarregando(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  useEffect(() => {
    if (!selecao.servico?.id) {
      setProfissionais([])
      return
    }
    setCarregandoProfissionais(true)
    getProfissionaisDoServico(tenant.id, selecao.servico.id)
      .then(profs => {
        setProfissionais(profs)
        setSelecao(prev => {
          if (prev.profissional && !profs.some(p => p.id === prev.profissional.id)) {
            return { ...prev, profissional: null }
          }
          return prev
        })
      })
      .catch(e => {
        console.error('Erro ao carregar profissionais:', e)
        toast.erro('Não foi possível carregar as profissionais disponíveis.')
      })
      .finally(() => setCarregandoProfissionais(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecao.servico?.id])

  const carregarSlots = useCallback(() => {
    if (!selecao.data || !selecao.servico || !config || profissionais.length === 0) return
    setCarregandoSlots(true)
    setSlots([])
    setProfissionaisPorSlot({})
    const dataObj = new Date(selecao.data + 'T12:00:00')
    if (selecao.profissional) {
      getSlotsDisponiveis(tenant.id, selecao.profissional.id, dataObj, selecao.servico.duracao_min, config, selecao.servico.categoria)
        .then(s => { setSlots(s); setProfissionaisPorSlot({}) })
        .catch(e => toast.erro(e.message))
        .finally(() => setCarregandoSlots(false))
    } else {
      getSlotsDisponiveisTodos(tenant.id, profissionais, dataObj, selecao.servico.duracao_min, config, selecao.servico.categoria)
        .then(s => { setSlots(s); setProfissionaisPorSlot(s._profissionaisPorSlot || {}) })
        .catch(e => toast.erro(e.message))
        .finally(() => setCarregandoSlots(false))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecao.data, selecao.profissional, selecao.servico, config, profissionais])

  useEffect(() => {
    if (passo === 4) carregarSlots()
  }, [passo, carregarSlots])

  // Cloudflare Turnstile (anti-bot) no passo de confirmação.
  const turnstileRef = useRef(null)
  const turnstileWidgetId = useRef(null)
  const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

  useEffect(() => {
    if (passo !== 5 || !SITE_KEY) return

    let cancelado = false
    setTurnstileIndisponivel(false)

    // Marca o Turnstile como indisponível → o booking avança sem token
    // (fail-open). Acontece se o script for bloqueado (ad-blocker / browser de
    // privacidade), o domínio não estiver autorizado na sitekey, ou a
    // Cloudflare estiver inacessível. O rate-limit por IP no servidor mantém
    // a proteção anti-flood. Também remove o widget: em erro, a própria
    // Cloudflare deixa um link "Troubleshooting" no contentor — como já
    // avançamos sem token, não faz sentido mostrar isso ao cliente.
    function marcarIndisponivel() {
      if (cancelado) return
      setTurnstileIndisponivel(true)
      if (window.turnstile && turnstileWidgetId.current !== null) {
        try { window.turnstile.remove(turnstileWidgetId.current) } catch { /* noop */ }
        turnstileWidgetId.current = null
      }
    }

    // Carrega o script da Cloudflare uma vez. Resolve a `false` se falhar.
    function carregarScript() {
      return new Promise((resolve) => {
        if (window.turnstile) return resolve(true)
        const existente = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')
        if (existente) {
          existente.addEventListener('load', () => resolve(true))
          existente.addEventListener('error', () => resolve(false))
          return
        }
        const s = document.createElement('script')
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
        s.async = true
        s.defer = true
        s.onload = () => resolve(true)
        s.onerror = () => resolve(false)
        document.head.appendChild(s)
      })
    }

    // Rede de segurança: se não houver token num curto prazo, libertamos o botão.
    const timeout = setTimeout(marcarIndisponivel, 8000)

    carregarScript().then((ok) => {
      if (cancelado) return
      if (!ok || !window.turnstile || !turnstileRef.current) {
        marcarIndisponivel()
        return
      }
      // Evita render duplicado (StrictMode / re-entrada no passo).
      if (turnstileWidgetId.current !== null) return
      try {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => {
            if (cancelado) return
            setTurnstileToken(token)
            setTurnstileIndisponivel(false)
          },
          'expired-callback': () => { if (!cancelado) setTurnstileToken(null) },
          'error-callback': () => marcarIndisponivel(),
        })
      } catch {
        marcarIndisponivel()
      }
    })

    return () => {
      cancelado = true
      clearTimeout(timeout)
      if (window.turnstile && turnstileWidgetId.current !== null) {
        try { window.turnstile.remove(turnstileWidgetId.current) } catch { /* noop */ }
        turnstileWidgetId.current = null
      }
      setTurnstileToken(null)
    }
  }, [passo, SITE_KEY])

  function avancar() { setPasso(p => p + 1) }
  function recuar() { toast.erro(null); setPasso(p => p - 1) }

  async function confirmar() {
    const erros = {}
    if (!selecao.cliente.nome.trim()) erros.nome = 'Por favor introduza o seu nome completo.'
    if (!selecao.cliente.telefone.trim()) erros.telefone = 'Por favor introduza o seu número de telefone.'
    else if (!validarTelefone(selecao.cliente.telefone)) erros.telefone = 'Introduza um número português válido (ex: 912 345 678).'
    if (!selecao.cliente.data_nascimento) erros.data_nascimento = 'Por favor introduza a sua data de nascimento.'
    if (selecao.cliente.nif.trim() && !/^\d{9}$/.test(selecao.cliente.nif.trim())) erros.nif = 'O NIF deve ter exactamente 9 dígitos.'
    if (!consentimento) erros.consentimento = 'É necessário aceitar os Termos e a Política de Privacidade para continuar.'
    if (Object.keys(erros).length > 0) {
      setErrosCampos(erros)
      return
    }
    // Turnstile é best-effort: só pedimos para aguardar enquanto o widget está
    // ativo e ainda não devolveu token. Se falhou/indisponível (ad-blocker,
    // domínio não autorizado, etc.), deixamos avançar sem token — o servidor
    // mantém o rate-limit por IP como proteção. Nunca trancamos o cliente.
    if (SITE_KEY && !turnstileToken && !turnstileIndisponivel) {
      toast.erro('Aguarde um instante — a verificação de segurança está a carregar.')
      return
    }
    setErrosCampos({})
    setEnviando(true)
    toast.erro(null)
    try {
      const profId = selecao.profissional?.id
        || profissionaisPorSlot[new Date(selecao.slot).toISOString()]?.[0]
        || profissionais[0]?.id
      const inicio = new Date(selecao.slot)
      const fim = new Date(inicio.getTime() + selecao.servico.duracao_min * 60000)

      const sala_id = await escolherSalaLivre(tenant.id, selecao.servico.categoria, inicio, fim)

      const clientePayload = {
        nome: selecao.cliente.nome.trim(),
        telefone: selecao.cliente.telefone.trim(),
        email: selecao.cliente.email.trim() || null,
        data_nascimento: selecao.cliente.data_nascimento || null,
        nif: selecao.cliente.nif.trim() || null,
      }
      const agendamentoPayload = {
        servico_id: selecao.servico.id,
        profissional_id: profId,
        sala_id,
        cliente_nome: selecao.cliente.nome.trim(),
        cliente_email: selecao.cliente.email.trim() || null,
        cliente_telefone: selecao.cliente.telefone.trim(),
        data_hora_inicio: inicio.toISOString(),
        data_hora_fim: fim.toISOString(),
        notas: selecao.cliente.notas.trim() || null,
        newsletter_opt_in: selecao.cliente.newsletter,
      }

      const profissionalEscolhido = selecao.profissional
          || profissionais.find(p => p.id === profissionaisPorSlot[new Date(selecao.slot).toISOString()]?.[0])
          || profissionais[0]

      const sinal = calcularSinal(selecao.servico, config)
      const precisaPagar = config?.stripe_ativo === 'true' && sinal > 0

      if (precisaPagar) {
        // Pagamento do sinal antes da confirmação: guardamos os detalhes para a
        // página de confirmação (o regresso do Stripe é um carregamento novo,
        // sem state de navegação) e redirecionamos para o checkout.
        sessionStorage.setItem('confirmacaoPendente', JSON.stringify({
          selecao,
          profissionalNome: profissionalEscolhido?.nome || 'Profissional',
          sinal,
        }))
        const checkoutUrl = await iniciarPagamento(tenant.id, clientePayload, agendamentoPayload, turnstileToken)
        window.location.assign(checkoutUrl)
        return
      }

      await criarMarcacaoPublica(tenant.id, clientePayload, agendamentoPayload, turnstileToken)
      navigate('/confirmacao', {
        state: {
          selecao,
          profissionalNome: profissionalEscolhido?.nome || 'Profissional',
        },
      })
    } catch (e) {
      if (e?.message?.includes('SLOT_BLOQUEADO')) {
        toast.erro('Esse horário deixou de estar disponível. Por favor escolha outro horário.')
      } else if (e?.message?.includes('SLOT_OCUPADO') || e?.message?.includes('SALA_OCUPADA')) {
        toast.erro('Esse horário acabou de ficar ocupado. Por favor escolha outro horário.')
      } else if (e?.message?.includes('Demasiadas marcações') || e?.message?.includes('data de nascimento')) {
        toast.erro(e.message)
      } else {
        toast.erro('Não foi possível criar a marcação. Por favor tente novamente.')
      }
      // O token Turnstile é de uso único — resetar para nova tentativa.
      setTurnstileToken(null)
      if (window.turnstile && turnstileWidgetId.current !== null) {
        try { window.turnstile.reset(turnstileWidgetId.current) } catch { /* noop */ }
      }
    } finally {
      setEnviando(false)
    }
  }

  if (carregando) {
    return <div style={{ padding: '64px 24px' }}><Loading /></div>
  }

  const diasTrabalho = config?.dias_trabalho?.split(',').map(Number) || [2, 3, 4, 5, 6]
  const hoje = format(new Date(), 'yyyy-MM-dd')
  const categorias = [...new Set(servicos.map(s => s.categoria))]

  return (
    <main style={{ maxWidth: '680px', margin: '0 auto', padding: '36px 24px 64px' }}>
      <BarraProgresso passo={passo} />

      {/* ── Passo 1 (sub-ecrã): Apresentação do serviço ── */}
      {passo === 1 && apresentacao && (
        <div>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-display-weight)',
            fontSize: 'clamp(30px, 8vw, 44px)',
            lineHeight: 1.1,
            color: 'var(--cor-principal)',
            margin: '0 0 14px',
          }}>{apresentacao.nome}</h2>

          {/* Duração + preço em pílulas; o preço ganha destaque com cor da marca. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '28px' }}>
            <span style={{
              padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 500,
              background: 'var(--cor-creme-escuro)', color: 'var(--cor-texto-suave)',
            }}>
              {apresentacao.duracao_real_min ?? apresentacao.duracao_min} min
            </span>
            {Number(apresentacao.preco) > 0 && (
              <span style={{
                padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600,
                background: 'rgba(139, 74, 47, 0.08)', color: 'var(--cor-principal)',
                border: '1px solid rgba(139, 74, 47, 0.20)',
              }}>
                {fmtEur(apresentacao.preco)}
              </span>
            )}
          </div>

          {/* Média + descrição lado a lado em ecrã largo; em telemóvel o texto
              quebra para baixo (flexWrap + minWidth força o empilhamento sem
              media query, coerente com os inline styles do projeto). */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '28px',
            alignItems: 'flex-start',
            marginBottom: '8px',
            justifyContent: apresentacao.midia_url ? 'flex-start' : 'center',
          }}>
            {apresentacao.midia_url && (
              <div style={{ flex: '0 0 auto', width: 'min(320px, 78vw)', maxWidth: '100%', margin: '0 auto' }}>
                {apresentacao.midia_tipo === 'video' ? (
                  <video
                    // Decorativo: arranca sozinho, em loop e sem som. `muted` +
                    // `playsInline` são necessários para o autoplay no iOS/Android.
                    ref={refVideoApresentacao}
                    src={apresentacao.midia_url}
                    muted
                    autoPlay
                    loop
                    playsInline
                    preload="auto"
                    style={{
                      width: '100%',
                      aspectRatio: '9 / 16',
                      objectFit: 'cover',
                      borderRadius: 'var(--raio-lg)',
                      background: '#000',
                      display: 'block',
                      boxShadow: '0 16px 40px rgba(139, 74, 47, 0.18)',
                    }}
                  />
                ) : (
                  <img
                    src={apresentacao.midia_url}
                    alt={apresentacao.nome}
                    style={{
                      width: '100%',
                      aspectRatio: '9 / 16',
                      objectFit: 'cover',
                      borderRadius: 'var(--raio-lg)',
                      display: 'block',
                      boxShadow: '0 16px 40px rgba(139, 74, 47, 0.18)',
                    }}
                  />
                )}
              </div>
            )}

            {apresentacao.descricao_detalhada && (
              <div style={{ flex: '1 1 260px', minWidth: '220px' }}>
                <p style={{
                  fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: 'var(--cor-ambar)', fontWeight: 600, margin: '0 0 10px',
                }}>
                  Sobre o tratamento
                </p>
                <p style={{
                  color: 'var(--cor-texto)',
                  fontSize: '15px',
                  lineHeight: 1.7,
                  margin: 0,
                  whiteSpace: 'pre-line',
                  maxWidth: '52ch',
                }}>
                  {apresentacao.descricao_detalhada}
                </p>
              </div>
            )}
          </div>

          {/* Espaçador para o conteúdo final não ficar tapado pela barra fixa. */}
          <div style={{ height: '92px' }} aria-hidden="true" />

          {/* CTA fixo: preço sempre visível + avançar, sem obrigar a scroll. */}
          <div style={{
            position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30,
            background: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderTop: '1px solid var(--cor-borda)',
            padding: '12px 20px calc(12px + env(safe-area-inset-bottom))',
          }}>
            <div style={{ maxWidth: '680px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => setApresentacao(null)}
                className="btn-secundario"
                aria-label="Voltar"
                style={{ flex: '0 0 auto', padding: '12px 16px' }}
              >←</button>
              <div style={{ flex: 1, minWidth: 0, lineHeight: 1.15 }}>
                <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)' }}>
                  {apresentacao.duracao_real_min ?? apresentacao.duracao_min} min
                </div>
                {Number(apresentacao.preco) > 0 && (
                  <div style={{ fontSize: '17px', fontWeight: 600, color: 'var(--cor-principal)' }}>
                    {fmtEur(apresentacao.preco)}
                  </div>
                )}
              </div>
              <button
                onClick={() => { setApresentacao(null); avancar() }}
                className="btn-primario"
                style={{ flex: '0 0 auto' }}
              >Continuar →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Passo 1: Serviço ── */}
      {passo === 1 && !apresentacao && (
        <div>
          <h2 style={{ marginBottom: '6px' }}>Escolha o Serviço</h2>
          <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '28px', fontSize: '14px' }}>
            Selecione o tratamento que pretende realizar.
          </p>
          {categorias.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              background: 'var(--cor-branco)',
              border: '1px solid var(--cor-borda)',
              borderRadius: 'var(--raio-lg)',
              color: 'var(--cor-texto-suave)',
            }}>
              <p style={{ fontSize: '32px', marginBottom: '12px' }}>⚙️</p>
              <p style={{ fontWeight: '500', marginBottom: '8px' }}>Nenhum serviço encontrado</p>
              <p style={{ fontSize: '13px' }}>
                Execute a migração SQL no Supabase para carregar o catálogo de serviços.
              </p>
            </div>
          )}

          {categorias.map(cat => (
            <div key={cat} style={{ marginBottom: '28px' }}>
              <p style={{
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--cor-texto-suave)',
                marginBottom: '10px',
              }}>
                {cat}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {servicos.filter(s => s.categoria === cat).map(s => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setSelecao(prev => ({ ...prev, servico: s, slot: null }))
                      // Com apresentação → mostra o sub-ecrã primeiro; senão segue
                      // direto para a escolha do profissional (comportamento atual).
                      if (temApresentacao(s)) setApresentacao(s)
                      else avancar()
                    }}
                    style={cardServico(selecao.servico?.id === s.id)}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: 'var(--cor-texto)', marginBottom: '3px' }}>
                        {s.nome}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', lineHeight: 1.4 }}>
                        {s.descricao?.length > 90 ? s.descricao.substring(0, 90) + '…' : s.descricao}
                      </div>
                    </div>
                    <span style={{
                      fontSize: '12px',
                      color: 'var(--cor-ambar)',
                      background: 'var(--cor-creme)',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}>
                      {s.duracao_real_min ?? s.duracao_min} min
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Passo 2: Profissional ── */}
      {passo === 2 && (
        <div>
          <h2 style={{ marginBottom: '6px' }}>Escolha a Profissional</h2>
          <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '28px', fontSize: '14px' }}>
            {profissionais.length > 1
              ? 'Pode escolher uma profissional específica ou sem preferência.'
              : profissionais.length === 1
                ? 'Profissional disponível para este serviço.'
                : 'Para este serviço, é necessário falar diretamente connosco.'}
          </p>

          {carregandoProfissionais ? (
            <Loading texto="A carregar profissionais..." />
          ) : profissionais.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '48px 24px',
              color: 'var(--cor-texto-suave)',
              background: 'var(--cor-branco)',
              border: '1px solid var(--cor-borda)',
              borderRadius: 'var(--raio-lg)',
            }}>
              <p style={{ fontSize: '36px', marginBottom: '12px' }}>📞</p>
              <p style={{ marginBottom: '24px', color: 'var(--cor-texto)' }}>
                Para marcar este serviço, por favor contacte o centro para mais informações.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                <button onClick={recuar} className="btn-secundario">Escolher outro serviço</button>
                {config?.telefone && (
                  <a
                    href={`tel:${config.telefone.replace(/\s+/g, '')}`}
                    className="btn-primario"
                    style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    📞 Telefonar para o centro
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {profissionais.length > 1 && (
                <button
                  onClick={() => {
                    setSelecao(prev => ({ ...prev, profissional: null }))
                    avancar()
                  }}
                  style={cardProfissional(selecao.profissional === null && passo > 2)}
                >
                  <div style={{
                    width: '42px', height: '42px', borderRadius: '50%',
                    background: 'var(--cor-creme-escuro)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '20px', flexShrink: 0,
                  }}>
                    ✨
                  </div>
                  <div style={{ marginLeft: '14px' }}>
                    <div style={{ fontWeight: '500', color: 'var(--cor-texto)' }}>Sem preferência</div>
                    <div style={{ fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
                      Primeira profissional disponível
                    </div>
                  </div>
                </button>
              )}

              {profissionais.map(p => (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelecao(prev => ({ ...prev, profissional: p }))
                    avancar()
                  }}
                  style={cardProfissional(selecao.profissional?.id === p.id)}
                >
                  {p.foto_url ? (
                    <img src={p.foto_url} alt="" style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: '42px', height: '42px', borderRadius: '50%',
                      background: p.cor_agenda,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: '700', fontSize: '17px', flexShrink: 0,
                    }}>
                      {p.nome.charAt(0)}
                    </div>
                  )}
                  <div style={{ marginLeft: '14px' }}>
                    <div style={{ fontWeight: '500', color: 'var(--cor-texto)' }}>{p.nome}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <BotaoNavegacao passo={passo} recuar={recuar} podeAvancar={false} />
        </div>
      )}

      {/* ── Passo 3: Data ── */}
      {passo === 3 && (
        <div>
          <h2 style={{ marginBottom: '6px' }}>Escolha a Data</h2>
          <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '28px', fontSize: '14px' }}>
            Dias de atendimento: <strong>{diasTrabalhoTexto(diasTrabalho)}</strong>
          </p>

          <div style={{
            background: 'var(--cor-branco)',
            border: '1px solid var(--cor-borda)',
            borderRadius: 'var(--raio-lg)',
            padding: '28px 24px',
          }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
              Data
            </label>
            <input
              type="date"
              min={hoje}
              value={selecao.data}
              onChange={e => {
                const valor = e.target.value
                if (!valor) { setErroData(null); setSelecao(prev => ({ ...prev, data: '', slot: null })); return }
                const d = new Date(valor + 'T12:00:00')
                const diaSemana = d.getDay() + 1
                if (!diasTrabalho.includes(diaSemana)) {
                  // Mensagem inline (não um alerta intrusivo): o seletor de data
                  // nativo no telemóvel pode disparar onChange ao abrir, e um
                  // toast apareceria "sozinho" sem o utilizador ter escolhido.
                  setErroData('O espaço não funciona neste dia. Por favor escolha outro dia.')
                  setSelecao(prev => ({ ...prev, data: '', slot: null }))
                  return
                }
                setErroData(null)
                setSelecao(prev => ({ ...prev, data: valor, slot: null }))
              }}
              style={{
                ...inputStyle,
                background: 'var(--cor-creme)',
                minHeight: '48px',
                cursor: 'pointer',
                maxWidth: '100%',
              }}
            />
            {erroData ? (
              <p style={{ marginTop: '8px', fontSize: '13px', color: '#DC2626' }}>
                {erroData}
              </p>
            ) : !selecao.data && (
              <p style={{ marginTop: '8px', fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
                Toque no campo acima para escolher a data
              </p>
            )}
          </div>

          <BotaoNavegacao passo={passo} recuar={recuar} avancar={avancar} podeAvancar={!!selecao.data} />
        </div>
      )}

      {/* ── Passo 4: Hora ── */}
      {passo === 4 && (
        <div>
          <h2 style={{ marginBottom: '6px' }}>Escolha a Hora</h2>
          <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '28px', fontSize: '14px' }}>
            {selecao.data && format(new Date(selecao.data + 'T12:00:00'), "d 'de' MMMM 'de' yyyy", { locale: pt })}
          </p>

          {carregandoSlots ? (
            <Loading texto="A verificar disponibilidade..." />
          ) : slots.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '48px 24px',
              color: 'var(--cor-texto-suave)',
              background: 'var(--cor-branco)',
              border: '1px solid var(--cor-borda)',
              borderRadius: 'var(--raio-lg)',
            }}>
              <p style={{ fontSize: '36px', marginBottom: '12px' }}>😔</p>
              <p style={{ marginBottom: '20px' }}>Não há horários disponíveis neste dia.</p>
              <button onClick={recuar} className="btn-secundario">Escolher outra data</button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
              gap: '10px',
            }}>
              {slots.map((slot, i) => {
                const selecionado = selecao.slot && slot.toISOString() === selecao.slot
                return (
                  <button
                    key={i}
                    onClick={() => {
                      setSelecao(prev => ({ ...prev, slot: slot.toISOString() }))
                      avancar()
                    }}
                    style={{
                      padding: '12px 8px',
                      border: `2px solid ${selecionado ? 'var(--cor-principal)' : 'var(--cor-borda)'}`,
                      borderRadius: 'var(--raio)',
                      background: selecionado ? 'var(--cor-creme)' : 'var(--cor-branco)',
                      color: 'var(--cor-texto)',
                      fontWeight: '500',
                      fontSize: '15px',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {format(slot, 'HH:mm')}
                  </button>
                )
              })}
            </div>
          )}

          {slots.length > 0 && (
            <BotaoNavegacao passo={passo} recuar={recuar} podeAvancar={false} />
          )}
          {slots.length === 0 && !carregandoSlots && (
            <div style={{ marginTop: '16px' }} />
          )}
        </div>
      )}

      {/* ── Passo 5: Dados + Confirmar ── */}
      {passo === 5 && (
        <div>
          <h2 style={{ marginBottom: '6px' }}>Os Seus Dados</h2>
          <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '28px', fontSize: '14px' }}>
            Preencha os seus dados para confirmar a marcação.
          </p>

          {/* Resumo da marcação */}
          <div style={{
            background: 'var(--cor-creme)',
            border: '1px solid var(--cor-borda)',
            borderRadius: 'var(--raio-lg)',
            padding: '16px 20px',
            marginBottom: '28px',
            fontSize: '14px',
          }}>
            <p style={{ fontWeight: '600', marginBottom: '10px', color: 'var(--cor-principal)' }}>
              Resumo da Marcação
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--cor-texto-suave)' }}>Serviço</span>
                <strong style={{ textAlign: 'right' }}>{selecao.servico?.nome}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--cor-texto-suave)' }}>Duração</span>
                <strong>{selecao.servico?.duracao_real_min ?? selecao.servico?.duracao_min} min</strong>
              </div>
              {Number(selecao.servico?.preco) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ color: 'var(--cor-texto-suave)' }}>Preço</span>
                  <strong>{fmtEur(selecao.servico.preco)}</strong>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--cor-texto-suave)' }}>Data</span>
                <strong>
                  {selecao.data && format(new Date(selecao.data + 'T12:00:00'), "d MMM yyyy", { locale: pt })}
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--cor-texto-suave)' }}>Hora</span>
                <strong>{selecao.slot && format(new Date(selecao.slot), 'HH:mm')}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--cor-texto-suave)' }}>Profissional</span>
                <strong>{selecao.profissional?.nome || 'Sem preferência'}</strong>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Campo
              label="Nome completo *"
              name="name"
              autoComplete="name"
              value={selecao.cliente.nome}
              onChange={v => { setSelecao(prev => ({ ...prev, cliente: { ...prev.cliente, nome: v } })); setErrosCampos(prev => ({ ...prev, nome: null })) }}
              placeholder="O seu nome completo"
              erro={errosCampos.nome}
            />
            <Campo
              label="Telefone *"
              type="tel"
              name="tel"
              autoComplete="tel"
              inputMode="tel"
              value={selecao.cliente.telefone}
              onChange={v => { setSelecao(prev => ({ ...prev, cliente: { ...prev.cliente, telefone: v } })); setErrosCampos(prev => ({ ...prev, telefone: null })) }}
              placeholder="912 345 678"
              erro={errosCampos.telefone || (selecao.cliente.telefone && selecao.cliente.telefone.replace(/\D/g, '').length >= 9 && !validarTelefone(selecao.cliente.telefone) ? 'Número inválido. Use 9XXXXXXXX ou formato internacional (+44...)' : null)}
            />
            <Campo
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              value={selecao.cliente.email}
              onChange={v => setSelecao(prev => ({ ...prev, cliente: { ...prev.cliente, email: v } }))}
              placeholder="email@exemplo.com"
            />
            <Campo
              label="Data de Aniversário *"
              type="date"
              name="bday"
              autoComplete="bday"
              value={selecao.cliente.data_nascimento}
              onChange={v => { setSelecao(prev => ({ ...prev, cliente: { ...prev.cliente, data_nascimento: v } })); setErrosCampos(prev => ({ ...prev, data_nascimento: null })) }}
              erro={errosCampos.data_nascimento}
            />
            <Campo
              label="NIF"
              type="text"
              name="nif"
              inputMode="numeric"
              value={selecao.cliente.nif}
              onChange={v => { setSelecao(prev => ({ ...prev, cliente: { ...prev.cliente, nif: v } })); setErrosCampos(prev => ({ ...prev, nif: null })) }}
              placeholder="000000000"
              maxLength={9}
              erro={errosCampos.nif}
            />
            <Campo
              label="Notas / Observações"
              value={selecao.cliente.notas}
              onChange={v => setSelecao(prev => ({ ...prev, cliente: { ...prev.cliente, notas: v } }))}
              placeholder="Alergias, preferências, outras informações…"
              multiline
              maxLength={500}
            />

            {/* Newsletter opt-in (só faz sentido com email preenchido) */}
            {selecao.cliente.email.trim() && (
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                cursor: 'pointer',
                padding: '14px 16px',
                background: 'var(--cor-creme)',
                borderRadius: 'var(--raio)',
                border: '1px solid var(--cor-borda)',
              }}>
                <input
                  type="checkbox"
                  checked={selecao.cliente.newsletter}
                  onChange={e => setSelecao(prev => ({ ...prev, cliente: { ...prev.cliente, newsletter: e.target.checked } }))}
                  style={{ marginTop: '2px', flexShrink: 0, width: '16px', height: '16px', accentColor: 'var(--cor-principal)' }}
                />
                <span style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', lineHeight: 1.5 }}>
                  Aceito receber novidades e promoções d{tenant?.nome ? `e ${tenant.nome}` : 'o espaço'} por email. <em>(opcional)</em>
                </span>
              </label>
            )}
          </div>

          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px',
            fontSize: '12px', color: 'var(--cor-texto-suave)',
            marginTop: '16px', lineHeight: 1.6, cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={consentimento}
              onChange={e => {
                setConsentimento(e.target.checked)
                if (e.target.checked) setErrosCampos(prev => ({ ...prev, consentimento: undefined }))
              }}
              style={{ marginTop: '3px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span>
              Aceito os{' '}
              <Link to="/termos" target="_blank" style={{ color: 'var(--cor-principal)' }}>
                Termos e Condições
              </Link>
              {' '}e a{' '}
              <Link to="/politica-privacidade" target="_blank" style={{ color: 'var(--cor-principal)' }}>
                Política de Privacidade
              </Link>
              . Os seus dados são utilizados exclusivamente para a gestão da sua marcação.
            </span>
          </label>
          {errosCampos.consentimento && (
            <p style={{ color: '#DC2626', fontSize: '12px', marginTop: '6px' }}>
              {errosCampos.consentimento}
            </p>
          )}

          {SITE_KEY && !turnstileIndisponivel && (
            <div ref={turnstileRef} style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }} />
          )}

          <NotaTeste style={{ marginTop: '20px' }}>
            esta marcação é apenas um teste. Não será realizada e não tem custos.
          </NotaTeste>

          <div className="acao-form" style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <button onClick={recuar} className="btn-secundario" style={{ flexShrink: 0 }}>
              ← Voltar
            </button>
            <button
              onClick={confirmar}
              className="btn-primario"
              disabled={enviando}
              style={{ flex: 1, opacity: enviando ? 0.6 : 1 }}
            >
              {enviando ? 'A confirmar…' : MODO_TESTE ? 'Confirmar Marcação (teste)' : 'Confirmar Marcação'}
            </button>
          </div>
        </div>
      )}
    </main>
  )
}
